import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import NoteCard from "../components/NoteCard";
import { getBlock, notesInBlock } from "../data/notes";

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function BlockPage() {
  const { blockSlug } = useParams();
  const block = getBlock(blockSlug);

  if (!block) {
    return (
      <div className="py-20 px-4 text-center">
        <p className="text-muted mb-4">That block doesn’t exist.</p>
        <Link to="/projects" className="text-accent hover:text-accent-hover">
          ← Back to Projects
        </Link>
      </div>
    );
  }

  const items = notesInBlock(blockSlug);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      className="py-10 md:py-14 px-4 md:px-8"
    >
      <Helmet>
        <title>{block.title} | Soham Desai</title>
        <meta name="description" content={block.blurb} />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <motion.div variants={fade}>
          <Link
            to="/projects"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Projects
          </Link>
        </motion.div>

        <motion.h1
          variants={fade}
          className="font-display text-3xl md:text-4xl font-semibold text-foreground border-l-2 border-accent pl-4 mb-4"
        >
          {block.title}
        </motion.h1>
        <motion.p variants={fade} className="text-muted-foreground mb-2 max-w-3xl leading-relaxed">
          {block.blurb}
        </motion.p>
        <motion.p variants={fade} className="text-muted-foreground text-xs uppercase tracking-wide mb-10">
          {items.length} {items.length === 1 ? "note" : "notes"}
        </motion.p>

        {items.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((note) => (
              <NoteCard key={note.slug} note={note} />
            ))}
          </div>
        ) : (
          <motion.div
            variants={fade}
            className="rounded-lg border border-dashed border-border bg-surface/40 p-10 text-center"
          >
            <p className="text-muted">No notes here yet, coming soon.</p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
